import { useId, useState } from 'react';

import type { DraftAnnotation } from './document';
import { FIELD_NAME_LABELS } from './fieldLabels';
import type { ReviewFieldName } from './schema';
import styles from './review.module.css';

export interface AnnotationsPanelProps {
  readonly title: string;
  readonly annotations: readonly DraftAnnotation[];
  readonly fieldChoices: readonly ReviewFieldName[];
  readonly editable: boolean;
  readonly onAdd: (text: string, fieldName: ReviewFieldName | null) => void;
  readonly onRemove: (clientId: string) => void;
}

/**
 * The owner's own remarks, note-level or attached to one row. `docs/implementation/
 * 06-mobile-web.md` 6.4 lists annotations beside the other editable parts of the document; this
 * is the one shared component for both placements.
 */
export function AnnotationsPanel({
  title,
  annotations,
  fieldChoices,
  editable,
  onAdd,
  onRemove,
}: AnnotationsPanelProps) {
  const textareaId = useId();
  const selectId = useId();
  const [text, setText] = useState('');
  const [fieldName, setFieldName] = useState<ReviewFieldName | ''>('');

  function handleAdd(): void {
    if (text.trim() === '') {
      return;
    }
    onAdd(text, fieldName === '' ? null : fieldName);
    setText('');
    setFieldName('');
  }

  return (
    <div className={styles.field}>
      <span className={styles.label}>{title}</span>
      {annotations.length > 0 ? (
        <ul className={styles.annotationsList}>
          {annotations.map((annotation) => (
            <li key={annotation.clientId} className={styles.annotationItem}>
              <span className={styles.annotationText}>
                {annotation.fieldName !== null
                  ? `${FIELD_NAME_LABELS[annotation.fieldName]}: `
                  : ''}
                {annotation.text}
              </span>
              {editable ? (
                <button
                  type="button"
                  className={styles.iconButtonDanger}
                  aria-label="Eliminar nota"
                  onClick={() => {
                    onRemove(annotation.clientId);
                  }}
                >
                  ×
                </button>
              ) : null}
            </li>
          ))}
        </ul>
      ) : null}
      {editable ? (
        <div className={styles.annotationForm}>
          <label className={styles.label} htmlFor={textareaId}>
            Agregar nota
          </label>
          <textarea
            id={textareaId}
            className={styles.textarea}
            value={text}
            maxLength={500}
            placeholder="Escribe una observación"
            onChange={(event) => {
              setText(event.target.value);
            }}
          />
          {fieldChoices.length > 0 ? (
            <>
              <label className={styles.label} htmlFor={selectId}>
                Relacionada con (opcional)
              </label>
              <select
                id={selectId}
                className={styles.select}
                value={fieldName}
                onChange={(event) => {
                  setFieldName(event.target.value as ReviewFieldName | '');
                }}
              >
                <option value="">Ninguno en particular</option>
                {fieldChoices.map((choice) => (
                  <option key={choice} value={choice}>
                    {FIELD_NAME_LABELS[choice]}
                  </option>
                ))}
              </select>
            </>
          ) : null}
          <button
            type="button"
            className={styles.linkButton}
            disabled={text.trim() === ''}
            onClick={handleAdd}
          >
            Agregar nota
          </button>
        </div>
      ) : null}
    </div>
  );
}
